/** Document titles stay within a SERP title. Descriptions stay within a snippet. */
export const SEO_TITLE_MAX = 60;
export const META_DESCRIPTION_MAX = 155;
export const SEO_DESCRIPTION_MAX = 160;

/**
 * Keep a document title within SEO_TITLE_MAX without adding words.
 * Titles that already end in " | Fixome" keep that suffix.
 */
export function fitSeoTitle(title, max = SEO_TITLE_MAX) {
  const cleaned = String(title || '').replace(/\s+/g, ' ').trim();
  if (cleaned.length <= max) return cleaned;
  const suffix = ' | Fixome';
  if (cleaned.endsWith(suffix) && suffix.length < max) {
    const room = max - suffix.length;
    const head = cleaned.slice(0, cleaned.length - suffix.length);
    const slice = head.slice(0, room + 1);
    const lastSpace = slice.lastIndexOf(' ');
    const trimmed = (lastSpace > 12 ? slice.slice(0, lastSpace) : head.slice(0, room)).replace(
      /[|,\-:\s]+$/,
      ''
    );
    return `${trimmed}${suffix}`;
  }
  const slice = cleaned.slice(0, max + 1);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 20 ? slice.slice(0, lastSpace) : cleaned.slice(0, max)).replace(/[|,\-:\s]+$/, '');
}

export const AC_SERVICE_KERALA_SLUG =
  'ac-service-and-hvac-maintenance-in-kerala-a-complete-guide';

export const AC_SERVICE_KERALA_SEO = {
  title: 'AC Service Kerala | HVAC Maintenance Guide | Fixome',
  description:
    'AC service Kerala: a practical guide to home AC and HVAC maintenance in Kochi, Thiruvananthapuram, and Calicut. Book AC service with Fixome.',
};

export const BLOGS_INDEX_SEO = {
  title: 'Kerala Home Service Guides | Fixome Blog',
  description:
    'Guides for Kerala home services from Fixome, including plumbing, electrical, AC, cleaning, and upkeep in Kochi, Thiruvananthapuram, and Calicut.',
};

export const HELP_PAGE_SEO = {
  title: 'Help with Kerala Home Services | Fixome',
  description:
    'How to book Fixome home services in Kerala. Choose Emergency, Same day (24h), or Within 48 hours. Support in Kochi, Thiruvananthapuram, and Calicut.',
};

export const OFFERS_PAGE_SEO = {
  title: 'AC Service in Kerala | Fixome Offers',
  description:
    'Book AC service and repair with verified Fixome technicians. Kerala home services for households in Kochi, Thiruvananthapuram, and Calicut.',
};

/** Hand-shortened document titles. The post title stays the on-page heading. */
const BLOG_DOCUMENT_TITLES = {
  'home-appliance-repair-in-kerala-fix-or-replace-a-practical-cost-guide':
    'Appliance Repair in Kerala: Fix or Replace? | Fixome',
  'home-security-in-kerala-upgrading-your-locks-and-entry-points':
    'Home Security in Kerala: Locks and Entry | Fixome',
  'moving-home-in-kerala-a-complete-stress-free-relocation-guide':
    'Moving Home in Kerala: A Relocation Guide | Fixome',
  'pest-control-in-kerala-a-guide-to-termites-and-monsoon-pests':
    'Pest Control in Kerala: Termites and Monsoon | Fixome',
  'home-painting-in-kerala-the-complete-guide-to-colours-paints-and-timing':
    'Home Painting in Kerala: Colours, Paints, Timing | Fixome',
  'deep-cleaning-your-kerala-home-why-once-a-year-is-not-enough':
    'Why Kerala Homes Need More Deep Cleaning | Fixome',
  'monsoon-plumbing-guide-protecting-your-kerala-home-this-rainy-season':
    'Monsoon Plumbing Guide for Kerala Homes | Fixome',
  'electrical-safety-at-home-what-every-kerala-homeowner-should-know':
    'Electrical Safety for Kerala Homeowners | Fixome',
  'why-is-electricity-bill-so-high':
    'Why Is Your Electricity Bill So High? | Fixome',
  'home-maintenance-services-kerala-checklist':
    'Home Maintenance Services in Kerala: Checklist | Fixome',
  'home-waterproofing-services-kerala-monsoon-damp-walls':
    'Home Waterproofing Services in Kerala: Damp Walls | Fixome',
  'house-painting-services-kerala-7-decisions-before-you-paint':
    'House Painting Services in Kerala: 7 Decisions | Fixome',
};

export function trimMetaDescription(text, max = META_DESCRIPTION_MAX) {
  const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
  if (cleaned.length <= max) return cleaned;
  const slice = cleaned.slice(0, max + 1);
  const lastSpace = slice.lastIndexOf(' ');
  const trimmed = (lastSpace > 40 ? slice.slice(0, lastSpace) : cleaned.slice(0, max)).replace(
    /[.,;:\s]+$/,
    ''
  );
  return trimmed;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Kerala dates without locale data, so the prerender and the browser
 * print the same string and hydration does not rewrite the node.
 */
export function formatBlogDate(value, style = 'short') {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const ist = new Date(parsed.getTime() + (5 * 60 + 30) * 60 * 1000);
  const months = style === 'long' ? LONG_MONTHS : SHORT_MONTHS;
  return `${ist.getUTCDate()} ${months[ist.getUTCMonth()]} ${ist.getUTCFullYear()}`;
}

export function blogExcerpt(blog) {
  if (!blog) return '';
  return blog.excerpt || blog.description || '';
}

/**
 * Document title and meta description for a published post.
 * The AC guide uses a stronger query-focused title; every other post uses its own title and excerpt.
 */
export function blogDocumentMeta(blog, slug) {
  const resolvedSlug = slug || blog?.slug || '';
  if (resolvedSlug === AC_SERVICE_KERALA_SLUG) {
    return {
      title: AC_SERVICE_KERALA_SEO.title,
      description: AC_SERVICE_KERALA_SEO.description,
    };
  }

  const titleText = String(blog?.title || 'Article').replace(/\s+/g, ' ').trim() || 'Article';
  const excerpt = trimMetaDescription(blogExcerpt(blog));
  const composed = BLOG_DOCUMENT_TITLES[resolvedSlug] || `${titleText} | Fixome`;
  return {
    title: fitSeoTitle(composed),
    description: excerpt || fitSeoTitle(`${titleText}. A Kerala home services guide from Fixome`, SEO_DESCRIPTION_MAX),
  };
}
